import ProBadge from 'components/badges/ProBadge'
import { ReactNode } from 'react'
import { highestActiveTier, paddlePlanToTier, Tier } from 'utils/api/accounts'
import { withObservables } from '@nozbe/watermelondb/react'
import ObserveService from 'services/db/Observe'

import * as Styled from './styled'
import { Model } from 'types/model'
import { AccountDoc } from 'types/api/changes'
import Logger from 'services/Logger'
import { usePayment } from 'components/providers/PaymentProvider'
import { tierToPlan } from 'components/tier/TierHover'
import { Box } from '@mui/system'
import { PlanName } from 'components/dialogs/payment/PlansInfoDialog'

import { tierFeaturesToAnalyticsFeature } from 'components/tier/OutOfTierDialog'
import { theme } from 'theme'
import { Spacing } from '@material-ui/core/styles/createSpacing'
import { TierFeatures } from 'utils/api/group_state'
import { isCypress } from 'utils/cypress'
import i18n from 'i18n'
import { InfoOutlined } from '@mui/icons-material'
import { Tooltip } from '@mui/material'

interface PopupSectionRowProps {
  title: ReactNode
  value?: ReactNode
  icon?: ReactNode
  onlyTitle?: boolean
  action?: () => void
  color?: string
  valueColor?: string
  iconClass?: string
  neededTier?: Tier
  account?: Model<AccountDoc> | AccountDoc
  tierFeature?: TierFeatures
  titleColor?: string
  overflow?: string
  width?: string
  maxWidthIcon?: number
  cursor?: 'pointer' | 'default'
  spacing?: Spacing | string
  actionId?: string
  svgHeight?: string
  actionBg?: boolean
  gap?: string
  valuePlaceholder?: boolean
  flexGrowForRow?: number
  rowHeight?: string
  actionPadding?: string
  clickableTitle?: boolean
  engulfEntireRow?: boolean
  minWidthIcon?: string
  explanation?: string
}

export function PopupSectionRow(props: PopupSectionRowProps) {
  const activeTier = highestActiveTier(props.account)

  const needBlockUser =
    props.neededTier != undefined && activeTier < props.neededTier
  const { showPaymentDialog } = usePayment()

  const handleClose = (
    status: 'PURCHASED' | 'PURCHASE_ABORTED' | 'DIALOG_CLOSED',
    plan: PlanName
  ) => {
    Logger.debug('plan status; plan; =>', status, plan)

    const shouldHandleAction =
      paddlePlanToTier(plan) >= (props.neededTier ?? Tier.FREE)

    if (status === 'DIALOG_CLOSED') {
      if (props.action && shouldHandleAction) props.action()
    }
  }
  const openPayments = () => {
    if (props.neededTier !== undefined) {
      Logger.blue('Purchases:', props.neededTier, activeTier)
      showPaymentDialog(tierToPlan(props.neededTier), handleClose, [
        tierFeaturesToAnalyticsFeature(props.tierFeature)
      ])
    }
  }

  return (
    <Styled.PopupSectionRow
      data-onlytitle={props.onlyTitle}
      data-hasactionbg={props.actionBg ? 'true' : undefined}
      sx={{
        '& svg': {
          width: 'auto',
          maxWidth: props.maxWidthIcon ?? 16,
          height: props.svgHeight ?? '100%',
          color: props.color ?? theme.palette.input.placeholder
        },
        cursor: 'default',
        justifyContent: props.flexGrowForRow ? 'space-between' : undefined
      }}
    >
      {props.icon && (
        <span
          onClick={(e) => {
            e.preventDefault()
          }}
          data-content={props.iconClass ?? 'icon'}
        >
          {props.icon}
        </span>
      )}
      <Box
        sx={{
          flexGrow: props.flexGrowForRow ?? 1,
          minHeight: props.rowHeight ?? '30px',
          width: props.width ?? 'auto',
          display: 'flex',
          alignItems: 'center',
          borderRadius: props.engulfEntireRow ? '8px' : undefined,
          paddingRight:
            props.engulfEntireRow && i18n.dir() === 'ltr' ? '8px' : undefined,
          paddingLeft:
            props.engulfEntireRow && i18n.dir() === 'rtl' ? '8px' : undefined,
          '&:hover': {
            backgroundColor: props.engulfEntireRow
              ? theme.palette.background.paper
              : undefined
          }
        }}
      >
        <Box
          id={props.actionId}
          display={'flex'}
          flexDirection={'row'}
          style={{
            color: props.titleColor,
            textOverflow: 'ellipsis',
            overflow: props.overflow ? props.overflow : undefined,
            flexGrow: 1,
            maxHeight: props.explanation
              ? 'fit-content'
              : (props.rowHeight ?? '30px'),
            borderRadius:
              props.actionBg || props.engulfEntireRow ? '8px' : '0px',
            cursor:
              props.actionBg ||
              props.engulfEntireRow ||
              props.clickableTitle ||
              isCypress()
                ? 'pointer'
                : 'default',
            padding:
              props.actionPadding ??
              (props.actionBg || props.engulfEntireRow
                ? theme.spacing(1, 1.5)
                : undefined)
          }}
          onClick={
            props.actionBg || props.clickableTitle || isCypress()
              ? needBlockUser
                ? openPayments
                : props.action
              : undefined
          }
          data-content={props.action ? 'action' : 'title'}
        >
          {needBlockUser ? (
            <ProBadge title={props.title} neededTier={props.neededTier} />
          ) : (
            props.title
          )}
          {props.explanation && (
            <ExplanationTooltip title={props.explanation} />
          )}
        </Box>
        {props.value && (
          <div style={{ width: 'fit-content' }}>
            <div
              style={{
                color: props.valueColor,
                cursor: props.cursor ?? 'pointer'
              }}
              onClick={needBlockUser ? openPayments : props.action}
            >
              {props.value}
            </div>
            {needBlockUser && (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={openPayments}
                position={'absolute'}
                width={'fit-content'}
                height={'fit-content'}
              >
                <span style={{ pointerEvents: 'none', opacity: 0 }}>
                  {props.value}
                </span>
              </Box>
            )}
          </div>
        )}
      </Box>
      {props.valuePlaceholder && <Box width={'20px'}></Box>}
    </Styled.PopupSectionRow>
  )
}

export default withObservables([], () => ({
  account: ObserveService.Account()
}))(PopupSectionRow)

export function ExplanationTooltip(props: { title: string }) {
  return (
    <Tooltip
      sx={{
        marginLeft: i18n.dir() === 'ltr' ? 1 : 0,
        marginRight: i18n.dir() === 'rtl' ? 1 : 0
      }}
      arrow
      slotProps={{
        tooltip: {
          sx: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignContent: 'center',
            padding: '12px',
            fontSize: '15px',
            lineHeight: '22px',
            letterSpacing: '0.5px',
            margin: '4px !important',
            borderRadius: '20px',
            fontWeight: 400,

            backgroundColor: 'white',
            color: 'black',
            boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.25)',
            '& .MuiTooltip-arrow::before': {
              backgroundColor: 'white !important'
            }
          }
        },
        popper: {
          sx: {
            zIndex: 999998
          }
        }
      }}
      title={props.title}
      placement="top"
    >
      <InfoOutlined
        sx={{
          marginLeft: i18n.dir() === 'ltr' ? 1 : 0,
          marginRight: i18n.dir() === 'rtl' ? 1 : 0,
          width: '16px !important',
          height: '16px !important'
        }}
      />
    </Tooltip>
  )
}
